import type GIAbstractManager from 'src/lib/geo_item/GIAbstractManager';
import type EMAbstractLayerManager from 'src/lib/extreme_map/EMAbstractLayerManager';
import type GICommentManager from 'src/lib/geo_item/GICommentManager';
import type EMCommentLayerManager from 'src/lib/extreme_map/EMCommentLayerManager';
import type { Comment } from 'src/models/comment';
import type { Detection } from 'src/models/extLandAiDetections';
import type { Emergency } from 'src/models/extLandApEmergency';
import type { CarWithoutDevice, CarDevice } from 'src/models/cars';
import type { TenkenData } from 'src/models/extJunkaiTenkenReportTenkenData';
import type { Mtx, MtxMovie } from 'src/models/mtx';
import type { Kilopost, Movie, MovieGeoIndex, MovieList } from 'src/models/movie';
import type { Location } from 'src/models';

export interface GeoItemMeta {
  name: string;
  dispName: string;
  manager: GIAbstractManager;
  layerManager: EMAbstractLayerManager;
  hideToggleCheckbox: boolean;
  isCreatable: boolean;
  isTabPanel: boolean;
  tabLabel: string;
}

export interface GeoItemMetaComment extends GeoItemMeta {
  manager: GICommentManager;
  layerManager: EMCommentLayerManager;
}

export interface GeoItemsMeta {
  list: GeoItemMeta[];
  map: Record<string, GeoItemMeta>;
  show: Record<string, boolean>;
  order: Record<string, number>;
  countsDisp: Record<string, string>;
}

export const initGeoItemsMeta = (): GeoItemsMeta => ({
  list: [],
  map: {},
  show: {},
  order: {},
  countsDisp: {},
});

export interface GIKp extends Omit<Kilopost, 'kp' | 'kpCalc' | 'location'> {
  kp: number;
  kpCalc: number;
  location: Location;
  placeNameDisp?: string;
  roadNameDispShort?: string;
  // getNearKps が距離計算後に付与する場所表示文字列。生成元によっては未設定のため optional とする。
  locationDisp?: string;
}

export interface GIMovieGeoIndex extends Omit<MovieGeoIndex, 'kilopost'> {
  ts?: Date | null;
  // EMMovieLayerManager が location(生の緯度経度)から補間計算する地図表示用座標。
  interpolatedLocation?: Location;
  angle?: number | null;
  startMsecDiff?: number;
  endMsecDiff?: number;
  locationDisp?: string;
  kilopost: GIKp | null;
}

export interface GIMovie extends Omit<Movie, 'movieGeoIndices'> {
  ts?: Date | null;
  playStartOffsetMsec?: number;
  hardStartOffsetMsec?: number;
  hardEndOffsetMsec?: number;
  playDurationMsec?: number;
  accumMsec?: number;
  durationMsec?: number;
  playEndOffsetMsec?: number;
  movieGeoIndices: GIMovieGeoIndex[];
}

export interface GIMovieList extends Omit<MovieList, 'startKpUid' | 'endKpUid' | 'startTs' | 'endTs' | 'movies'> {
  startKp: GIKp;
  endKp: GIKp;
  startTs: Date | null;
  endTs: Date | null;
  movies: GIMovie[];
  isSelected: boolean;
  dateLocationCarDisp: string;
  dateLocationDisp: string;
  searchType: string;
  showTimeTexts?: boolean;
  mightCalculateClosestMgi?: boolean;
  isPreCalculated?: boolean;
  fullDurationMsec?: number;
  hardStartTs?: Date;
  hardEndTs?: Date;
  startOffset?: number;
}

export interface GIComment extends Omit<Comment, 'ts' | 'location'> {
  ts: Date;
  location: Location;
  commentTypeDisp: string;
  iconPath: string;
  shareScopeDisp: string;
  contentDisp: string;
  isParentComment: boolean;
  isChildComment: boolean;
  commentTypeColorCode: string;
  isSelected: boolean;
  updateTs: boolean;
  saveFiles: File[];
  removeFileIds: number[];
}

export interface GIDetection extends Omit<Detection, 'location'> {
  id: number;
  location: Location;
  detectionKindDisp: 'ポットホール' | 'ヒビ' | 'ジョイント損傷';
  jointTypesDisp: string;
  isSelected: boolean;
  dateDisp?: string;
  commentDisp?: string;
}

export interface GIEmergency extends Omit<Emergency, 'location'> {
  location: Location;
  isSelected: boolean;
}

interface GIDevice extends CarDevice {
  carKindDisp: string;
}

export interface GICar extends CarWithoutDevice {
  device: GIDevice;
  isMoving: boolean;
  isSelected?: boolean | null;
  hideCarIcon?: boolean | null;
  carZIndex?: number | null;
  trailZIndex?: number | null;
}

export interface GITenkenData extends Omit<TenkenData, 'location'> {
  inspectionTs: Date;
  location: Location;
  isSelected: boolean;
  titleDisp: string;
  kpDisp: string;
  contentDisp: string;
  // GI 構築時に条件付きで付与する表示用文字列のため optional とする。
  landmarkDisp?: string;
}

export interface GIPoint {
  id: string;
  location: Location;
  showWeak?: boolean;
  text?: string;
  isSelected?: boolean;
}

export interface GIDebugMTX {
  mtxs: Mtx[];
  movies: MtxMovie[];
}

export type GIResourceWithID = GIComment | GITenkenData | GIDetection | GIEmergency;

export type GIResource = GIResourceWithID | GICar | GIMovieList | GIPoint;

// 表示中のレイヤーデータが再取得された際に、選択中要素がまだ存在するか確認するためのパラメータ
export interface TryRefreshCurrentSelectedElemInfoParams {
  dataName: string;
  resources: GIResource[];
  idFieldName?: string;
}

// 地図要素の選択・レイヤーアイテムクリック時のオプション
export interface MapSelectOpts {
  moveCenter?: boolean;
}

